import React from "react";
import cogoToast from "cogo-toast";
import { useState, useEffect } from "react";
import {
  createScoringCriteriaApi,
  listScoringCriteriaApi,
  updateScoringCriteriaApi,
  deleteScoringCriteriaApi,
  createScoringRuleApi,
  listScoringRulesApi,
  updateScoringRuleApi,
  deleteScoringRuleApi,
  listScoringPeriodsApi,
} from "../../services/Api";
import Select from "react-select";
import { SUCCESS_RESULT } from "../../Constants";
import { FaTimes, FaEdit, FaPlus, FaTrash, FaSearch, FaInfoCircle, FaList } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  Input,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Badge,
} from "reactstrap";
import BtnSpinner from "../../components/BtnSpinner";
import Swal from "sweetalert2";

const StaffScoringCriteria = (props) => {
  const history = useHistory();
  const [is_processing, setProcessing] = useState(false);
  const [criteriaList, setCriteriaList] = useState([]);
  const [creatingCriteria, setCreatingCriteria] = useState(false);
  const [updatingCriteria, setUpdatingCriteria] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    max_score: 10,
    weight_percentage: 0,
    description: "",
    scoring_period_id: "",
  });
  const [periodsList, setPeriodsList] = useState([]);
  const [selectedPeriodFilter, setSelectedPeriodFilter] = useState(null);
  const [show_create_dialog, setShow_create_dialog] = useState(false);
  const [show_update_dialog, setShow_update_dialog] = useState(false);
  const [criteriaToUpdate, setCriteriaToUpdate] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [show_rules_modal, setShow_rules_modal] = useState(false);
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [show_create_rule_dialog, setShow_create_rule_dialog] = useState(false);
  const [show_update_rule_dialog, setShow_update_rule_dialog] = useState(false);
  const [creatingRule, setCreatingRule] = useState(false);
  const [updatingRule, setUpdatingRule] = useState(false);
  const [ruleFormData, setRuleFormData] = useState({
    criteria_id: "",
    min_score: 0,
    max_score: 0,
    description: "",
    order: 1,
  });
  const [ruleToUpdate, setRuleToUpdate] = useState({});

  useEffect(() => {
    fetch_periods();
    fetch_criteria_list();
    return () => {};
  }, []);

  useEffect(() => {
    fetch_criteria_list();
  }, [selectedPeriodFilter]);

  const fetch_periods = () => {
    listScoringPeriodsApi({ status: 10001 })
      .then((res) => {
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setPeriodsList(response.data || []);
          }
        }
      })
      .catch((e) => {
        console.error("Failed to load periods:", e);
      });
  };

  const fetch_criteria_list = () => {
    setProcessing(true);
    const payload = selectedPeriodFilter
      ? { scoring_period_id: selectedPeriodFilter.value }
      : {};
    listScoringCriteriaApi(payload)
      .then((res) => {
        setProcessing(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setCriteriaList(response.data || []);
          } else {
            cogoToast.error(response.message || "Failed to load criteria", {
              hideAfter: 5,
            });
            setCriteriaList([]);
          }
        } else {
          cogoToast.error(res.message || "Failed to load criteria", {
            hideAfter: 5,
          });
          setCriteriaList([]);
        }
      })
      .catch((e) => {
        setProcessing(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
        setCriteriaList([]);
      });
  };

  const calculateTotalWeight = (periodId = null) => {
    // If periodId is provided, calculate for that period only
    // Otherwise, use the selected period filter or all criteria
    const criteriaToCalculate = periodId
      ? criteriaList.filter((c) => c.scoring_period_id === periodId)
      : selectedPeriodFilter && selectedPeriodFilter.value
      ? criteriaList.filter((c) => c.scoring_period_id === selectedPeriodFilter.value)
      : criteriaList;
    
    return criteriaToCalculate.reduce((sum, criteria) => sum + (parseFloat(criteria.weight_percentage) || 0), 0);
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      max_score: 10,
      weight_percentage: 0,
      description: "",
      scoring_period_id: "",
    });
  };

  const resetRuleForm = () => {
    setRuleFormData({
      criteria_id: "",
      min_score: 0,
      max_score: 0,
      description: "",
      order: 1,
    });
  };

  const create_criteria = (e) => {
    e.preventDefault();

    if (!formData.code || formData.code.trim() === "") {
      cogoToast.error("Code is required", { hideAfter: 3 });
      return;
    }

    if (!formData.name || formData.name.trim() === "") {
      cogoToast.error("Name is required", { hideAfter: 3 });
      return;
    }

    if (!formData.max_score || formData.max_score <= 0) {
      cogoToast.error("Max score must be greater than 0", { hideAfter: 3 });
      return;
    }

    if (!formData.weight_percentage || formData.weight_percentage <= 0) {
      cogoToast.error("Weight percentage must be greater than 0", { hideAfter: 3 });
      return;
    }

    if (!formData.scoring_period_id) {
      cogoToast.error("Scoring period is required", { hideAfter: 3 });
      return;
    }

    // Check if adding this weight would exceed 100% for the selected period
    const periodId = formData.scoring_period_id;
    const currentTotal = calculateTotalWeight(periodId);
    const newTotal = currentTotal + parseFloat(formData.weight_percentage);
    if (newTotal > 100) {
      cogoToast.error(`Total weight percentage cannot exceed 100%. Current total for this period: ${currentTotal.toFixed(2)}%`, {
        hideAfter: 5,
      });
      return;
    }

    setCreatingCriteria(true);

    let data = {
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      max_score: parseInt(formData.max_score),
      weight_percentage: parseFloat(formData.weight_percentage),
      description: formData.description ? formData.description.trim() : "",
      scoring_period_id: formData.scoring_period_id,
    };

    createScoringCriteriaApi(data)
      .then((res) => {
        setCreatingCriteria(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setShow_create_dialog(false);
            resetForm();
            fetch_criteria_list();
            cogoToast.success(response.message || "Criteria created successfully", {
              hideAfter: 3,
            });
          } else {
            cogoToast.error(response.message || "Failed to create criteria", {
              hideAfter: 5,
            });
          }
        } else {
          cogoToast.error(res.message || "Failed to create criteria", {
            hideAfter: 5,
          });
        }
      })
      .catch((e) => {
        setCreatingCriteria(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const update_criteria = (e) => {
    e.preventDefault();

    if (!criteriaToUpdate.id) {
      cogoToast.error("Criteria ID is missing", { hideAfter: 3 });
      return;
    }

    if (!criteriaToUpdate.code || criteriaToUpdate.code.trim() === "") {
      cogoToast.error("Code is required", { hideAfter: 3 });
      return;
    }

    if (!criteriaToUpdate.name || criteriaToUpdate.name.trim() === "") {
      cogoToast.error("Name is required", { hideAfter: 3 });
      return;
    }

    if (!criteriaToUpdate.max_score || criteriaToUpdate.max_score <= 0) {
      cogoToast.error("Max score must be greater than 0", { hideAfter: 3 });
      return;
    }

    if (!criteriaToUpdate.weight_percentage || criteriaToUpdate.weight_percentage <= 0) {
      cogoToast.error("Weight percentage must be greater than 0", { hideAfter: 3 });
      return;
    }

    if (!criteriaToUpdate.scoring_period_id) {
      cogoToast.error("Scoring period is required", { hideAfter: 3 });
      return;
    }

    // Check if updating this weight would exceed 100% for the selected period
    const periodId = criteriaToUpdate.scoring_period_id;
    // Get current total for this period, excluding the current criteria being updated
    const currentTotal = criteriaList
      .filter((c) => c.scoring_period_id === periodId && c.id !== criteriaToUpdate.id)
      .reduce((sum, criteria) => sum + (parseFloat(criteria.weight_percentage) || 0), 0);
    const newTotal = currentTotal + parseFloat(criteriaToUpdate.weight_percentage);
    if (newTotal > 100) {
      cogoToast.error(`Total weight percentage cannot exceed 100%. Current total for this period: ${currentTotal.toFixed(2)}%`, {
        hideAfter: 5,
      });
      return;
    }

    setUpdatingCriteria(true);

    let data = {
      id: criteriaToUpdate.id,
      code: criteriaToUpdate.code.trim().toUpperCase(),
      name: criteriaToUpdate.name.trim(),
      max_score: parseInt(criteriaToUpdate.max_score),
      weight_percentage: parseFloat(criteriaToUpdate.weight_percentage),
      description: criteriaToUpdate.description ? criteriaToUpdate.description.trim() : "",
      scoring_period_id: criteriaToUpdate.scoring_period_id,
    };

    updateScoringCriteriaApi(data)
      .then((res) => {
        setUpdatingCriteria(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setShow_update_dialog(false);
            setCriteriaToUpdate({});
            fetch_criteria_list();
            cogoToast.success(response.message || "Criteria updated successfully", {
              hideAfter: 3,
            });
          } else {
            cogoToast.error(response.message || "Failed to update criteria", {
              hideAfter: 5,
            });
          }
        } else {
          cogoToast.error(res.message || "Failed to update criteria", {
            hideAfter: 5,
          });
        }
      })
      .catch((e) => {
        setUpdatingCriteria(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const delete_criteria = (criteria) => {
    Swal.fire({
      title: "Delete Criteria?",
      text: `Are you sure you want to delete "${criteria.name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteScoringCriteriaApi({ id: criteria.id })
          .then((res) => {
            if (res.status === 200) {
              const response = res.data;
              if (response.status === SUCCESS_RESULT) {
                fetch_criteria_list();
                cogoToast.success(response.message || "Criteria deleted successfully", {
                  hideAfter: 3,
                });
              } else {
                cogoToast.error(response.message || "Failed to delete criteria", {
                  hideAfter: 5,
                });
              }
            } else {
              cogoToast.error(res.message || "Failed to delete criteria", {
                hideAfter: 5,
              });
            }
          })
          .catch((e) => {
            const errorMessage =
              e?.response?.data?.message || "Something went wrong, please try again.";
            cogoToast.error(errorMessage, { hideAfter: 5 });
          });
      }
    });
  };

  const open_rules_modal = (criteria) => {
    setSelectedCriteria(criteria);
    setShow_rules_modal(true);
    fetch_rules(criteria.id);
  };

  const fetch_rules = (criteriaId) => {
    setLoadingRules(true);
    listScoringRulesApi({ criteria_id: criteriaId })
      .then((res) => {
        setLoadingRules(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setRules(response.data || []);
          } else {
            cogoToast.error(response.message || "Failed to load rules", {
              hideAfter: 5,
            });
            setRules([]);
          }
        } else {
          cogoToast.error(res.message || "Failed to load rules", {
            hideAfter: 5,
          });
          setRules([]);
        }
      })
      .catch((e) => {
        setLoadingRules(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
        setRules([]);
      });
  };

  const open_create_rule_dialog = () => {
    if (!selectedCriteria) return;
    setRuleFormData({
      criteria_id: selectedCriteria.id,
      min_score: 0,
      max_score: selectedCriteria.max_score || 10,
      description: "",
      order: rules.length + 1,
    });
    setShow_create_rule_dialog(true);
  };

  const create_rule = (e) => {
    e.preventDefault();

    if (!ruleFormData.criteria_id) {
      cogoToast.error("Criteria ID is missing", { hideAfter: 3 });
      return;
    }

    if (ruleFormData.min_score < 0 || ruleFormData.max_score < 0) {
      cogoToast.error("Scores cannot be negative", { hideAfter: 3 });
      return;
    }

    if (ruleFormData.min_score > ruleFormData.max_score) {
      cogoToast.error("Min score cannot be greater than max score", { hideAfter: 3 });
      return;
    }

    if (!ruleFormData.description || ruleFormData.description.trim() === "") {
      cogoToast.error("Description is required", { hideAfter: 3 });
      return;
    }

    // Check if score range overlaps with existing rules
    const selectedCriteriaData = criteriaList.find(c => c.id === ruleFormData.criteria_id);
    if (selectedCriteriaData) {
      const maxPossibleScore = selectedCriteriaData.max_score;
      if (ruleFormData.max_score > maxPossibleScore) {
        cogoToast.error(`Max score cannot exceed criteria max score (${maxPossibleScore})`, {
          hideAfter: 5,
        });
        return;
      }
    }

    setCreatingRule(true);

    let data = {
      criteria_id: ruleFormData.criteria_id,
      min_score: parseFloat(ruleFormData.min_score),
      max_score: parseFloat(ruleFormData.max_score),
      description: ruleFormData.description.trim(),
      order: parseInt(ruleFormData.order),
    };

    createScoringRuleApi(data)
      .then((res) => {
        setCreatingRule(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setShow_create_rule_dialog(false);
            resetRuleForm();
            fetch_rules(ruleFormData.criteria_id);
            cogoToast.success(response.message || "Rule created successfully", {
              hideAfter: 3,
            });
          } else {
            cogoToast.error(response.message || "Failed to create rule", {
              hideAfter: 5,
            });
          }
        } else {
          cogoToast.error(res.message || "Failed to create rule", {
            hideAfter: 5,
          });
        }
      })
      .catch((e) => {
        setCreatingRule(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const open_update_rule_dialog = (rule) => {
    setRuleToUpdate({ ...rule });
    setShow_update_rule_dialog(true);
  };

  const update_rule = (e) => {
    e.preventDefault();

    if (!ruleToUpdate.id) {
      cogoToast.error("Rule ID is missing", { hideAfter: 3 });
      return;
    }

    if (ruleToUpdate.min_score < 0 || ruleToUpdate.max_score < 0) {
      cogoToast.error("Scores cannot be negative", { hideAfter: 3 });
      return;
    }

    if (ruleToUpdate.min_score > ruleToUpdate.max_score) {
      cogoToast.error("Min score cannot be greater than max score", { hideAfter: 3 });
      return;
    }

    if (!ruleToUpdate.description || ruleToUpdate.description.trim() === "") {
      cogoToast.error("Description is required", { hideAfter: 3 });
      return;
    }

    setUpdatingRule(true);

    let data = {
      id: ruleToUpdate.id,
      criteria_id: ruleToUpdate.criteria_id,
      min_score: parseFloat(ruleToUpdate.min_score),
      max_score: parseFloat(ruleToUpdate.max_score),
      description: ruleToUpdate.description.trim(),
      order: parseInt(ruleToUpdate.order),
    };

    updateScoringRuleApi(data)
      .then((res) => {
        setUpdatingRule(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setShow_update_rule_dialog(false);
            setRuleToUpdate({});
            fetch_rules(ruleToUpdate.criteria_id);
            cogoToast.success(response.message || "Rule updated successfully", {
              hideAfter: 3,
            });
          } else {
            cogoToast.error(response.message || "Failed to update rule", {
              hideAfter: 5,
            });
          }
        } else {
          cogoToast.error(res.message || "Failed to update rule", {
            hideAfter: 5,
          });
        }
      })
      .catch((e) => {
        setUpdatingRule(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const delete_rule = (rule) => {
    Swal.fire({
      title: "Delete Rule?",
      text: `Are you sure you want to delete this rule? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteScoringRuleApi({ id: rule.id })
          .then((res) => {
            if (res.status === 200) {
              const response = res.data;
              if (response.status === SUCCESS_RESULT) {
                fetch_rules(rule.criteria_id);
                cogoToast.success(response.message || "Rule deleted successfully", {
                  hideAfter: 3,
                });
              } else {
                cogoToast.error(response.message || "Failed to delete rule", {
                  hideAfter: 5,
                });
              }
            } else {
              cogoToast.error(res.message || "Failed to delete rule", {
                hideAfter: 5,
              });
            }
          })
          .catch((e) => {
            const errorMessage =
              e?.response?.data?.message || "Something went wrong, please try again.";
            cogoToast.error(errorMessage, { hideAfter: 5 });
          });
      }
    });
  };

  const filteredCriteria = criteriaList.filter((criteria) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      criteria.code?.toLowerCase().includes(searchLower) ||
      criteria.name?.toLowerCase().includes(searchLower) ||
      criteria.description?.toLowerCase().includes(searchLower)
    );
  });

  const totalWeight = calculateTotalWeight(selectedPeriodFilter?.value);

  return (
    <div style={{ padding: "20px" }}>
      <div className="page-header d-print-none mb-4">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Staff Scoring Criteria</h2>
              <div className="text-muted mt-1">Manage scoring criteria and rules for staff evaluation</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xl">
        <Card className="shadow-sm mb-4">
          <CardHeader>
            <Row>
              <Col md={5}>
                <Label for="search">Search</Label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    id="search"
                    placeholder="Search criteria..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </Col>
              <Col md={4}>
                <Label for="period_filter">Filter by Period</Label>
                <Select
                  id="period_filter"
                  value={selectedPeriodFilter}
                  onChange={(selected) => {
                    setSelectedPeriodFilter(selected);
                  }}
                  options={[
                    { value: "", label: "All Periods" },
                    ...periodsList.map((period) => ({
                      value: period.id,
                      label: period.name,
                    })),
                  ]}
                  placeholder="Select period..."
                  isClearable
                  isSearchable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                    }),
                  }}
                />
              </Col>
              <Col md={3} className="text-end">
                <Label>&nbsp;</Label>
                <div>
                  <Button
                    color="primary"
                    onClick={() => {
                      resetForm();
                      setShow_create_dialog(true);
                    }}
                    style={{
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                    }}
                  >
                    <FaPlus style={{ marginRight: "8px" }} />
                    Create Criteria
                  </Button>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {is_processing ? (
              <div className="text-center py-5">
                <BtnSpinner loading={true} />
                <p className="mt-3 text-muted">Loading criteria...</p>
              </div>
            ) : filteredCriteria.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">
                  {searchTerm ? "No criteria found matching your search" : "No criteria found"}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <Badge color={totalWeight === 100 ? "success" : totalWeight > 100 ? "danger" : "warning"}>
                    Total Weight: {totalWeight.toFixed(2)}%
                  </Badge>
                  {totalWeight !== 100 && (
                    <span className="ms-2 text-muted">
                      {totalWeight < 100
                        ? `Need ${(100 - totalWeight).toFixed(2)}% more to reach 100%`
                        : `Exceeds 100% by ${(totalWeight - 100).toFixed(2)}%`}
                    </span>
                  )}
                </div>
                <div className="table-responsive">
                  <Table hover striped bordered>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Period</th>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Max Score</th>
                        <th>Weight %</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCriteria.map((criteria, index) => (
                        <tr key={criteria.id}>
                          <td>{index + 1}</td>
                          <td>
                            {criteria?.period_name || "N/A"}
                          </td>
                          <td>
                            <strong>{criteria.code}</strong>
                          </td>
                          <td>{criteria.name}</td>
                          <td>{criteria.max_score}</td>
                          <td>
                            <Badge color="info">{criteria.weight_percentage}%</Badge>
                          </td>
                          <td>{criteria.description || "N/A"}</td>
                          <td>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => open_rules_modal(criteria)}
                                title="Manage Rules"
                              >
                                <FaList />
                              </Button>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => {
                                  setCriteriaToUpdate({ ...criteria });
                                  setShow_update_dialog(true);
                                }}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => delete_criteria(criteria)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Create Criteria Modal */}
      <Modal isOpen={show_create_dialog} toggle={() => setShow_create_dialog(false)} size="lg">
        <ModalHeader toggle={() => setShow_create_dialog(false)}>Create Scoring Criteria</ModalHeader>
        <Form onSubmit={create_criteria}>
          <ModalBody>
            <FormGroup>
              <Label for="scoring_period_id">Scoring Period *</Label>
              <Select
                id="scoring_period_id"
                value={
                  formData.scoring_period_id
                    ? {
                        value: formData.scoring_period_id,
                        label: periodsList.find((p) => p.id === formData.scoring_period_id)?.name || "",
                      }
                    : null
                }
                onChange={(selected) =>
                  setFormData({
                    ...formData,
                    scoring_period_id: selected ? selected.value : "",
                  })
                }
                options={periodsList
                  .filter((p) => p.status === 10001)
                  .map((period) => ({
                    value: period.id,
                    label: period.name,
                  }))}
                placeholder="Select scoring period..."
                isSearchable
                isClearable
                required
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "38px",
                  }),
                }}
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="code">Code *</Label>
                  <Input
                    type="text"
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="e.g., PERFORMANCE"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="name">Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Performance"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="max_score">Max Score *</Label>
                  <Input
                    type="number"
                    id="max_score"
                    value={formData.max_score}
                    onChange={(e) =>
                      setFormData({ ...formData, max_score: parseInt(e.target.value) || 0 })
                    }
                    min="1"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="weight_percentage">
                    Weight Percentage * (Remaining: {100 - totalWeight}%)
                  </Label>
                  <Input
                    type="number"
                    id="weight_percentage"
                    value={formData.weight_percentage}
                    onChange={(e) =>
                      setFormData({ ...formData, weight_percentage: parseFloat(e.target.value) || 0 })
                    }
                    min="0"
                    max={100 - totalWeight}
                    step="0.01"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="Enter description..."
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShow_create_dialog(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={creatingCriteria}>
              {creatingCriteria ? <BtnSpinner loading={true} /> : "Create"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Update Criteria Modal */}
      <Modal isOpen={show_update_dialog} toggle={() => setShow_update_dialog(false)} size="lg">
        <ModalHeader toggle={() => setShow_update_dialog(false)}>Update Scoring Criteria</ModalHeader>
        <Form onSubmit={update_criteria}>
          <ModalBody>
            <FormGroup>
              <Label for="update_scoring_period_id">Scoring Period *</Label>
              <Select
                id="update_scoring_period_id"
                value={
                  criteriaToUpdate.scoring_period_id
                    ? {
                        value: criteriaToUpdate.scoring_period_id,
                        label: periodsList.find((p) => p.id === criteriaToUpdate.scoring_period_id)?.name || "",
                      }
                    : null
                }
                onChange={(selected) =>
                  setCriteriaToUpdate({
                    ...criteriaToUpdate,
                    scoring_period_id: selected ? selected.value : "",
                  })
                }
                options={periodsList
                  .filter((p) => p.status === 10001)
                  .map((period) => ({
                    value: period.id,
                    label: period.name,
                  }))}
                placeholder="Select scoring period..."
                isSearchable
                isClearable
                required
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "38px",
                  }),
                }}
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="update_code">Code *</Label>
                  <Input
                    type="text"
                    id="update_code"
                    value={criteriaToUpdate.code || ""}
                    onChange={(e) =>
                      setCriteriaToUpdate({ ...criteriaToUpdate, code: e.target.value.toUpperCase() })
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="update_name">Name *</Label>
                  <Input
                    type="text"
                    id="update_name"
                    value={criteriaToUpdate.name || ""}
                    onChange={(e) =>
                      setCriteriaToUpdate({ ...criteriaToUpdate, name: e.target.value })
                    }
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="update_max_score">Max Score *</Label>
                  <Input
                    type="number"
                    id="update_max_score"
                    value={criteriaToUpdate.max_score || ""}
                    onChange={(e) =>
                      setCriteriaToUpdate({
                        ...criteriaToUpdate,
                        max_score: parseInt(e.target.value) || 0,
                      })
                    }
                    min="1"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="update_weight_percentage">
                    Weight Percentage * (Remaining: {criteriaToUpdate.scoring_period_id ? (100 - criteriaList.filter((c) => c.scoring_period_id === criteriaToUpdate.scoring_period_id && c.id !== criteriaToUpdate.id).reduce((sum, criteria) => sum + (parseFloat(criteria.weight_percentage) || 0), 0)).toFixed(2) : (100 - totalWeight).toFixed(2)}%)
                  </Label>
                  <Input
                    type="number"
                    id="update_weight_percentage"
                    value={criteriaToUpdate.weight_percentage || ""}
                    onChange={(e) =>
                      setCriteriaToUpdate({
                        ...criteriaToUpdate,
                        weight_percentage: parseFloat(e.target.value) || 0,
                      })
                    }
                    min="0"
                    max={criteriaToUpdate.scoring_period_id ? (100 - criteriaList.filter((c) => c.scoring_period_id === criteriaToUpdate.scoring_period_id && c.id !== criteriaToUpdate.id).reduce((sum, criteria) => sum + (parseFloat(criteria.weight_percentage) || 0), 0)) : (100 - totalWeight)}
                    step="0.01"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label for="update_description">Description</Label>
              <Input
                type="textarea"
                id="update_description"
                value={criteriaToUpdate.description || ""}
                onChange={(e) =>
                  setCriteriaToUpdate({ ...criteriaToUpdate, description: e.target.value })
                }
                rows="3"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShow_update_dialog(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={updatingCriteria}>
              {updatingCriteria ? <BtnSpinner loading={true} /> : "Update"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Rules Modal */}
      <Modal
        isOpen={show_rules_modal}
        toggle={() => {
          setShow_rules_modal(false);
          setSelectedCriteria(null);
          setRules([]);
        }}
        size="lg"
      >
        <ModalHeader toggle={() => {
          setShow_rules_modal(false);
          setSelectedCriteria(null);
          setRules([]);
        }}>
          Manage Rules: {selectedCriteria?.name}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Button
              color="primary"
              size="sm"
              onClick={open_create_rule_dialog}
            >
              <FaPlus style={{ marginRight: "8px" }} />
              Create Rule
            </Button>
          </div>
          {loadingRules ? (
            <div className="text-center py-3">
              <BtnSpinner loading={true} />
              <p className="mt-2 text-muted">Loading rules...</p>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-3">
              <p className="text-muted">No rules found. Create your first rule.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Min Score</th>
                    <th>Max Score</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((rule, index) => (
                      <tr key={rule.id}>
                        <td>{rule.order || index + 1}</td>
                        <td>{rule.min_score}</td>
                        <td>{rule.max_score}</td>
                        <td>{rule.description}</td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => open_update_rule_dialog(rule)}
                              title="Edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => delete_rule(rule)}
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShow_rules_modal(false);
              setSelectedCriteria(null);
              setRules([]);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Rule Modal */}
      <Modal
        isOpen={show_create_rule_dialog}
        toggle={() => setShow_create_rule_dialog(false)}
      >
        <ModalHeader toggle={() => setShow_create_rule_dialog(false)}>Create Rule</ModalHeader>
        <Form onSubmit={create_rule}>
          <ModalBody>
            <FormGroup>
              <Label for="rule_min_score">Min Score *</Label>
              <Input
                type="number"
                id="rule_min_score"
                value={ruleFormData.min_score}
                onChange={(e) =>
                  setRuleFormData({ ...ruleFormData, min_score: parseFloat(e.target.value) || 0 })
                }
                min="0"
                max={selectedCriteria?.max_score || 10}
                step="0.1"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="rule_max_score">
                Max Score * (Criteria Max: {selectedCriteria?.max_score || 10})
              </Label>
              <Input
                type="number"
                id="rule_max_score"
                value={ruleFormData.max_score}
                onChange={(e) =>
                  setRuleFormData({ ...ruleFormData, max_score: parseFloat(e.target.value) || 0 })
                }
                min="0"
                max={selectedCriteria?.max_score || 10}
                step="0.1"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="rule_description">Description *</Label>
              <Input
                type="textarea"
                id="rule_description"
                value={ruleFormData.description}
                onChange={(e) =>
                  setRuleFormData({ ...ruleFormData, description: e.target.value })
                }
                rows="3"
                placeholder="e.g., Excellent performance"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="rule_order">Order *</Label>
              <Input
                type="number"
                id="rule_order"
                value={ruleFormData.order}
                onChange={(e) =>
                  setRuleFormData({ ...ruleFormData, order: parseInt(e.target.value) || 1 })
                }
                min="1"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShow_create_rule_dialog(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={creatingRule}>
              {creatingRule ? <BtnSpinner loading={true} /> : "Create"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Update Rule Modal */}
      <Modal
        isOpen={show_update_rule_dialog}
        toggle={() => setShow_update_rule_dialog(false)}
      >
        <ModalHeader toggle={() => setShow_update_rule_dialog(false)}>Update Rule</ModalHeader>
        <Form onSubmit={update_rule}>
          <ModalBody>
            <FormGroup>
              <Label for="update_rule_min_score">Min Score *</Label>
              <Input
                type="number"
                id="update_rule_min_score"
                value={ruleToUpdate.min_score || ""}
                onChange={(e) =>
                  setRuleToUpdate({ ...ruleToUpdate, min_score: parseFloat(e.target.value) || 0 })
                }
                min="0"
                step="0.1"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="update_rule_max_score">Max Score *</Label>
              <Input
                type="number"
                id="update_rule_max_score"
                value={ruleToUpdate.max_score || ""}
                onChange={(e) =>
                  setRuleToUpdate({ ...ruleToUpdate, max_score: parseFloat(e.target.value) || 0 })
                }
                min="0"
                step="0.1"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="update_rule_description">Description *</Label>
              <Input
                type="textarea"
                id="update_rule_description"
                value={ruleToUpdate.description || ""}
                onChange={(e) =>
                  setRuleToUpdate({ ...ruleToUpdate, description: e.target.value })
                }
                rows="3"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="update_rule_order">Order *</Label>
              <Input
                type="number"
                id="update_rule_order"
                value={ruleToUpdate.order || ""}
                onChange={(e) =>
                  setRuleToUpdate({ ...ruleToUpdate, order: parseInt(e.target.value) || 1 })
                }
                min="1"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShow_update_rule_dialog(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={updatingRule}>
              {updatingRule ? <BtnSpinner loading={true} /> : "Update"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default StaffScoringCriteria;
